/**
 * This component handles the logic related to the multi select HTML element in advanced filters
 * in charge of manage information about marketing status filter.
 * Interactions with the store, specifically with the marketing status property of the search
 * parameters slice, should be placed here.
 * Also, code here any logic related to the bahavior of the multi select element for marketing
 * status filtering.
 */

import CheckboxGroup from '@components/checkbox';
import Panel from '@components/panel';
import { store, store$ } from '@redux/store';
import { distinctUntilChanged, map, tap, withLatestFrom } from 'rxjs';
import { searchParametersSlice } from '../redux/slices';
import { selectMarketingStatus } from '../redux/selectors';

const { actions } = searchParametersSlice;

const container = document.querySelector('[data-community-status-panel]');
const marketingStatusListContainer = container.querySelector('[data-group]');
const labelTextContainer = container.querySelector('[data-label-text]');
const allMarketingStatusOption = marketingStatusListContainer.querySelector('[data-default-all-status]');

/**
 * The observable to track changes in the multi select element for marketing status.
 */
const selectedOptions$ = new CheckboxGroup(marketingStatusListContainer).getObservable();

(() => new Panel({ container }))();

/**
 * dispatchMarketingStatus function will take the marketingStatus argument
 * and dispatch the fetchMarketingStatus to store it.
 * @param {string} marketingStatus is a comma separated string representing the selected marketing status.
 */
const dispatchMarketingStatus = (marketingStatus) => store.dispatch(actions.fetchMarketingStatus(marketingStatus));

/**
 * updateSelectedStatus function will update the marketing status multi select
 * HTML element depending in the informacion retrieved from the store related
 * to marketing statuses selected.
 * @param {Array} selectedStatuses to represent visually in the multi select.
 */
const updateSelectedStatus = (selectedStatuses) => {
  const allOptionInputs = marketingStatusListContainer.querySelectorAll('input[type="checkbox"]');
  allOptionInputs.forEach((option) => {
    if (selectedStatuses.includes(option.value)) {
      option.checked = true;
    } else {
      option.checked = false;
    }
    option.parentElement.classList.toggle('nhs-c-dropdown__option--active', option.checked);
  });

  allMarketingStatusOption.checked = selectedStatuses.length === 0;
  allMarketingStatusOption.parentElement.classList.toggle(
    'nhs-c-dropdown__option--active',
    allMarketingStatusOption.checked
  );
  labelTextContainer.classList.toggle('nhs-c-select-button--active', !allMarketingStatusOption.checked);
};

/**
 * updateCommunityStatusLabel function will update the label of the marketing status dropdown to
 * reflect the amount of selected options.
 * @param {Number} selectedCommunityStatusNumber Community Status selected amount.
 */
const updateCommunityStatusLabel = (selectedMarketingStatusNumber) => {
  if (selectedMarketingStatusNumber === 0) {
    labelTextContainer.innerText = labelTextContainer.dataset.defaultText;
    return;
  }

  labelTextContainer.innerText = `${selectedMarketingStatusNumber} Selected`;
};

/**
 * handleMarketingStatusChanges function will invoke functions in charge
 * of updating the multi select for marketing status to reflect store information.
 * @param {Array} selectedStatuses to represent visually in the multi select.
 */
const handleMarketingStatusChanges = (marketingStatuses) => {
  updateCommunityStatusLabel(marketingStatuses.length);
  updateSelectedStatus(marketingStatuses);
};

/**
 * onMarketingStatusChanged$ will emit a value every time marketing status string
 * changes from the search parameters slice in store.
 */
const onMarketingStatusChanged$ = store$.pipe(
  map(selectMarketingStatus),
  distinctUntilChanged(),
  map((statuses) => (statuses && statuses !== '' ? statuses?.split(',') : []))
);

/**
 * onSelectedMarketingStatus$ will emita a value every time the multi select for
 * marketing status changes in the UI.
 */
const onSelectedMarketingStatus$ = selectedOptions$.pipe(
  withLatestFrom(onMarketingStatusChanged$),
  map(([selectedOptions, marketingStatuses]) => {
    const shouldClearAll = marketingStatuses.length !== 0 && selectedOptions.includes(allMarketingStatusOption.value);
    return shouldClearAll
      ? ''
      : selectedOptions.filter((option) => option !== allMarketingStatusOption.value).join(',');
  }),
  tap((selectedOptions) => {
    if (selectedOptions === '') {
      allMarketingStatusOption.checked = true;
    }
  })
);

onSelectedMarketingStatus$.subscribe(dispatchMarketingStatus);
onMarketingStatusChanged$.subscribe(handleMarketingStatusChanges);
